import { Observable, batch, observable } from "@legendapp/state";
import { TracksBufferLoader } from "./snapshots-buffer/track-buffer-loader";
import { SnapshotBase } from "@/shared/types";
import { TRACK_SNAPSHOT_MAXIMUM_GAP_THRESHOLD } from "@/shared/consts";

export type TimeRange = { range: [number, number]; isEmpty?: boolean };

export class BufferTimeRanges<TSnapshot extends SnapshotBase> {
  public timeRanges$: Observable<TimeRange[]>;

  constructor(private bufferClient: TracksBufferLoader<TSnapshot>) {
    this.timeRanges$ = observable([]);
  }

  /**
   * Merges time ranges to keep buffered data time ranges
   * @param minDateTime
   * @param maxDateTime
   */

  public mergeTimeRanges(
    minDateTime: Date,
    maxDateTime: Date,
    isEmpty = false
  ) {
    const timeRanges = [...this.timeRanges$.peek()];

    let hasMerged = false;

    for (let index = 0; index < timeRanges.length; index++) {
      const [start, end] = timeRanges[index].range;

      // Avoid merging empty and non-empty time ranges
      if (
        (isEmpty === true && timeRanges[index].isEmpty === false) ||
        (isEmpty === false && timeRanges[index].isEmpty === true)
      ) {
        continue;
      }

      const minDateTimeEpoch = minDateTime.getTime();
      const maxDateTimeEpoch = maxDateTime.getTime();

      const isEndingPartInclusive =
        maxDateTimeEpoch >= start && minDateTimeEpoch < start;

      const isFullyInclusive =
        maxDateTimeEpoch >= end &&
        minDateTimeEpoch < end &&
        minDateTimeEpoch >= start;

      const isExlcusiveAndNear =
        minDateTimeEpoch - end <= TRACK_SNAPSHOT_MAXIMUM_GAP_THRESHOLD &&
        minDateTimeEpoch >= end;

      if (isEndingPartInclusive || isFullyInclusive || isExlcusiveAndNear) {
        const newStart = Math.min(start, minDateTimeEpoch);
        let newEnd = Math.max(end, maxDateTimeEpoch);

        // Merge overlapping ranges into single one
        if (timeRanges[index + 1] && newEnd >= timeRanges[index + 1].range[0]) {
          newEnd = timeRanges[index + 1].range[1];
          timeRanges.splice(index + 1, 1);
        }

        timeRanges[index] = {
          range: [newStart, newEnd],
          isEmpty,
        };
        hasMerged = true;

        break;
      }
    }

    if (!hasMerged) {
      timeRanges.push({
        range: [minDateTime.getTime(), maxDateTime.getTime()],
        isEmpty,
      });
    }

    // const trimmed = this.trimTimeRanges(timeRanges, 5);

    // if (trimmed) {
    //   timeRanges = trimmed;
    // }

    // timeRanges = this.mergeOverlappingIntervals(timeRanges);

    batch(() => {
      // console.log(timeRanges);
      this.timeRanges$.set(timeRanges);
      this.timeRanges$.sort(this.compareDates);
    });
  }

  public get totalDuration() {
    return this.timeRanges$
      .peek()
      .reduce((acc, range) => (acc += range.range[1] - range.range[0]), 0);
  }

  public trimTimeRanges(timeRanges: TimeRange[], durationInSec: number) {
    if (this.totalDuration >= 10 * 1000) {
      console.log(this.totalDuration);
      timeRanges[0].range[0] += durationInSec * 1000;
      this.bufferClient.flushBuffer(durationInSec);
      return timeRanges;
    }
  }

  private compareDates(a: TimeRange, b: TimeRange) {
    const dateA = a.range[0];
    const dateB = b.range[0];

    if (dateA < dateB) {
      return -1;
    } else if (dateA > dateB) {
      return 1;
    } else {
      return 0;
    }
  }

  private findNonEmptyTimeRangeIndex(currentTime: number) {
    const timeRanges = this.timeRanges$.peek();
    let findNextNonEmptyTimeRange = false;

    for (let i = 0; i < timeRanges.length; i++) {
      const {
        range: [start, end],
        isEmpty,
      } = timeRanges[i];

      if (!isEmpty && findNextNonEmptyTimeRange) {
        return {
          index: i,
          isEmpty,
        };
      }

      if (currentTime >= start && currentTime < end) {
        // If matching range is empty
        // Continue iteration until non-empty time range found
        if (isEmpty) {
          findNextNonEmptyTimeRange = true;
          continue;
        }

        return {
          index: i,
          isEmpty,
        };
      }
    }

    return {
      index: -1,
      isEmpty: false,
    };
  }

  private findAnyTimeRangeIndex(currentTime: number) {
    const timeRanges = this.timeRanges$.peek();

    for (let i = 0; i < timeRanges.length; i++) {
      const {
        range: [start, end],
        isEmpty,
      } = timeRanges[i];

      if (currentTime >= start && currentTime < end) {
        return {
          index: i,
          isEmpty,
        };
      }
    }

    return {
      index: -1,
      isEmpty: false,
    };
  }

  public findNextTimeRange(currentTime: number) {
    const timeRanges = this.timeRanges$.peek();
    const currentRange = this.findAnyTimeRangeIndex(currentTime);
    let result: TimeRange["range"] | null = null;

    if (currentRange.index < 0) return result;

    for (let i = currentRange.index + 1; i < timeRanges.length; i++) {
      if (!timeRanges[i].isEmpty) {
        result = timeRanges[i].range;
        break;
      }
    }

    return result;
  }

  public findBufferEnd(currentDateTime?: Date) {
    if (!currentDateTime) {
      const timeRanges = this.timeRanges$.peek();
      const lastRangeIndex = timeRanges.length - 1;
      return timeRanges[lastRangeIndex]
        ? new Date(timeRanges[lastRangeIndex].range[1])
        : null;
    }

    const { index: timeRangeIndex, isEmpty } = this.findNonEmptyTimeRangeIndex(
      currentDateTime.getTime()
    );

    if (timeRangeIndex > -1 && !isEmpty) {
      const [_, bufferEnd] = this.timeRanges$[timeRangeIndex].peek().range;

      return new Date(bufferEnd);
    }

    return null;
  }

  public isBufferAvailable(currentTime: number, includeEmptyRange = false) {
    let index = 0,
      isEmpty;

    if (includeEmptyRange) {
      ({ index, isEmpty } = this.findAnyTimeRangeIndex(currentTime));
    } else {
      ({ index, isEmpty } = this.findNonEmptyTimeRangeIndex(currentTime));
    }

    return [index > -1, isEmpty];
  }

  public clear() {
    this.timeRanges$.set([]);
  }
}
